'use client';

/**
 * Require Auth Component
 * Shows different content based on authentication state
 * Useful for showing login prompts instead of redirecting
 */

import React from 'react';
import { useAuth } from './AuthContext';

interface RequireAuthProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  showLoginPrompt?: boolean;
}

export function RequireAuth({
  children,
  fallback,
  showLoginPrompt = true,
}: RequireAuthProps) {
  const { isAuthenticated, isLoading, login } = useAuth();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange" />
      </div>
    );
  }

  if (!isAuthenticated) {
    if (fallback) {
      return <>{fallback}</>;
    }

    if (showLoginPrompt) {
      return (
        <div className="border border-cream/20 rounded-lg p-8 text-center">
          <h3 className="text-xl font-semibold text-cream mb-4">
            Authentication Required
          </h3>
          <p className="text-cream/80 mb-6">
            Please log in to access this feature.
          </p>
          <button
            onClick={() => login({ returnTo: window.location.pathname })}
            className="px-6 py-2 bg-orange text-navy rounded-lg hover:bg-orange/90 transition-colors font-semibold"
          >
            Log In
          </button>
        </div>
      );
    }

    return null;
  }

  return <>{children}</>;
}
